import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HERO } from "@/lib/heroImages";

/**
 * MirrorCard — the editorial homepage entry into The Mirror.
 *
 * The right side carries a real photographic "mirror" — still dawn water
 * (nature's own mirror) cropped into a soft oval, framed by a thin copper
 * ring and washed with the card's warm halo so it reads as reflective and
 * zen rather than cartoonish. The photograph rotates daily through the
 * same Mirror imagery bank as the /mirror page, so the card and the
 * destination visually echo each other.
 */
export default function MirrorCard() {
  const image = HERO.mirror;

  return (
    <Link
      to="/mirror"
      data-testid="mirror-home-card"
      className="group block relative overflow-hidden rounded-[24px] border hairline bg-[hsl(var(--paper))] p-7 md:p-10 tab-link hover:shadow-[0_10px_36px_-18px_hsl(var(--clay)/0.35)] transition-shadow"
    >
      {/* Warm halo bleeding in from the top-right so the mirror sits in light */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-70 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 90% 10%, rgba(225,146,113,0.14) 0%, transparent 55%)",
        }}
      />

      {/* Photographic "mirror" — still dawn water inside a soft oval */}
      <MirrorImagery image={image} />

      <div className="relative max-w-xl">
        <div className="text-[11px] uppercase tracking-[0.32em] text-[hsl(var(--copper))]">
          The Mirror
        </div>
        <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight tracking-tight text-[hsl(var(--fg))]">
          What's on your mind?
        </h2>
        <p className="mt-4 max-w-md text-[15px] leading-relaxed text-[hsl(var(--fg))]/80">
          A private space to talk it through, understand what you're feeling, and find what might
          support you.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))] group-hover:gap-3 transition-all">
          <span>Talk it through</span>
          <ArrowRight size={12} />
        </div>
      </div>
    </Link>
  );
}

/**
 * MirrorImagery — the photographic mirror on the right of the card.
 *
 * Oval-cropped photograph inside a thin copper ring, with a soft ivory
 * highlight along the top edge and a gentle warm wash so it never fights
 * the copy. Hidden on very small screens so the card stays readable.
 */
function MirrorImagery({ image }) {
  return (
    <div
      aria-hidden
      data-testid="mirror-home-card-imagery"
      className="pointer-events-none absolute right-4 md:right-10 top-1/2 -translate-y-1/2 hidden sm:block"
      style={{ width: "min(38%, 260px)", aspectRatio: "0.78 / 1" }}
    >
      {/* Soft floor shadow beneath the mirror */}
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 bottom-[-6px] rounded-full"
        style={{
          width: "72%",
          height: "12px",
          background: "radial-gradient(ellipse, rgba(58, 40, 30, 0.28), transparent 70%)",
          filter: "blur(3px)",
        }}
      />

      {/* Oval mask around the photograph. The copper ring + inner rim highlight
          are painted with layered box/inset shadows so the whole "mirror"
          is one clean element. */}
      <div
        className="relative w-full h-full overflow-hidden mirror-oval"
        style={{
          borderRadius: "50% / 50%",
          boxShadow: [
            // Copper frame ring
            "0 0 0 3px #B87352",
            "0 0 0 5px rgba(215,176,156,0.85)",
            // Depth
            "0 18px 36px -18px rgba(30,23,18,0.55)",
            // Inner rim shading so glass has weight (softer so photo shows)
            "inset 0 0 14px rgba(30,23,18,0.22)",
          ].join(", "),
        }}
      >
        {/* The photograph — still dawn water */}
        <div
          aria-hidden
          className="absolute inset-0 bg-center bg-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
          style={{ backgroundImage: `url("${image}")` }}
        />
        {/* Warm ivory wash — kept light so the reflection stays legible */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(160deg, rgba(251,243,228,0.10) 0%, rgba(225,146,113,0.08) 45%, rgba(30,23,18,0.18) 100%)",
          }}
        />
        {/* A slow drifting inner light — the reflection breathes on hover */}
        <div
          aria-hidden
          className="mirror-drift absolute -inset-[20%] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 30% 30%, rgba(255,244,224,0.55) 0%, rgba(255,244,224,0) 45%)",
            filter: "blur(6px)",
            mixBlendMode: "screen",
          }}
        />
        {/* Top-edge highlight — the soft "light on the glass" */}
        <div
          aria-hidden
          className="absolute inset-x-[8%] top-[6%] h-[22%] rounded-[50%]"
          style={{
            background:
              "radial-gradient(ellipse at 50% 0%, rgba(255,244,224,0.55), transparent 70%)",
            filter: "blur(2px)",
          }}
        />
      </div>
    </div>
  );
}
